import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { getSingleton, saveSingleton } from "@/lib/adminRepository";

// Tracking / code-injection admin page. Edits the site-wide header + body
// scripts stored in settings/tracking, injected on the public site by
// useSiteTracking(). Use for Google Tag Manager, Analytics, Meta Pixel, etc.
const BLANK = { headerScripts: "", bodyScripts: "" };

export default function TrackingPage() {
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSingleton("settings", "tracking");
        if (cancelled) return;
        if (data) setForm({ headerScripts: data.headerScripts || "", bodyScripts: data.bodyScripts || "" });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[tracking] load failed:", err);
        toast.error("Could not load tracking settings.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveSingleton("settings", "tracking", {
        headerScripts: form.headerScripts || "",
        bodyScripts: form.bodyScripts || "",
      });
      toast.success("Saved. Tracking scripts are now live on the public site.");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[tracking] save failed:", err);
      toast.error("Save failed: " + (err?.message || "unknown error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-tracking">
      <header className="mb-8">
        <div className="eyebrow text-bronze mb-2">Site</div>
        <h1 className="font-serif text-3xl md:text-4xl text-ink">Tracking &amp; Code Injection</h1>
        <p className="text-sm text-olive mt-2 max-w-xl">
          Add custom HTML / JavaScript site-wide — Google Tag Manager, Analytics,
          Meta Pixel, or tracking scripts. Injected on every page of the public site.
        </p>
      </header>

      <form onSubmit={handleSave} className="border border-bronze/20 bg-cream p-6 md:p-8 space-y-6 max-w-2xl" data-testid="admin-tracking-form">
        {loading ? (
          <p className="text-olive text-sm">Loading…</p>
        ) : (
          <>
            <label className="block">
              <span className="editorial-label">Header scripts</span>
              <span className="block text-xs text-olive/80 mb-2">Injected right before the closing <code>&lt;/head&gt;</code> tag on every page.</span>
              <textarea
                rows={6}
                className="editorial-input resize-y font-mono text-xs"
                value={form.headerScripts}
                onChange={update("headerScripts")}
                data-testid="admin-tracking-header"
                placeholder="<!-- e.g. Google Tag Manager / Analytics head snippet -->"
              />
            </label>

            <label className="block">
              <span className="editorial-label">Body scripts</span>
              <span className="block text-xs text-olive/80 mb-2">Injected right before the closing <code>&lt;/body&gt;</code> tag on every page.</span>
              <textarea
                rows={6}
                className="editorial-input resize-y font-mono text-xs"
                value={form.bodyScripts}
                onChange={update("bodyScripts")}
                data-testid="admin-tracking-body"
                placeholder="<!-- e.g. GTM <noscript> / chat widget / pixel -->"
              />
            </label>

            <p className="text-[11px] text-olive/70 leading-relaxed pt-2 border-t border-bronze/15">
              Leave a field blank to disable injection. Changes take effect on the next page load for visitors.
            </p>
          </>
        )}

        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={saving || loading}
            data-testid="admin-tracking-save"
            className="btn-primary disabled:opacity-60"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
